/**
 * EffectsIndividual.js
 * Section 1 of ResultEffects: one column per selected skill (#1, #2...),
 * sticky Effect column, horizontal scroll.
 * (No Total* — see EffectsColorTotals for aggregated totals)
 */
import React, { useState } from 'react';
import {
    getStyle, getEffectsFromScenario, collectEffectNames,
    stickyLeft, ScrollTable, EffectCell, SectionHeader, EffectNameCell,
} from './effectsHelpers';

const EffectsIndividual = ({ selectedSkills }) => {
    const [open, setOpen] = useState(true);
    const effectNames = collectEffectNames(selectedSkills);
    if (effectNames.length === 0) return null;

    return (
        <div className="space-y-2">
            <SectionHeader
                open={open}
                onToggle={() => setOpen(o => !o)}
                dotColor="bg-emerald-500"
                title="Individual Effects"
                subtitle={`${selectedSkills.length} skill${selectedSkills.length !== 1 ? 's' : ''} · ${effectNames.length} effect type${effectNames.length !== 1 ? 's' : ''}`}
            />
            {open && (
                <div className="rounded-xl border border-slate-700">
                    <ScrollTable minWidth={`${180 + selectedSkills.length * 90}px`}>
                        <thead>
                            <tr className="bg-slate-900 border-b border-slate-700">
                                <th className={`${stickyLeft} bg-slate-900 px-4 py-2.5 text-[9px]
                                    font-bold uppercase tracking-widest text-slate-500 w-44
                                    border-r border-slate-700/60`}>
                                    Effect
                                </th>
                                {selectedSkills.map((skill, idx) => (
                                    <th key={skill.Id_skill} className="px-4 py-2.5 text-center min-w-[90px]">
                                        <div className="flex flex-col items-center gap-0.5">
                                            <span className="inline-flex items-center justify-center
                                                w-5 h-5 rounded-full text-[9px] font-bold
                                                bg-violet-900/60 text-violet-300 border border-violet-700/50">
                                                #{idx + 1}
                                            </span>
                                            {skill.color && (
                                                <span className={`text-[8px] px-1 py-0.5 rounded border ${getStyle(skill.color).pill}`}>
                                                    {skill.color.replace(' Skills', '').replace(' Skill', '')}
                                                </span>
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {effectNames.map((name, rowIdx) => (
                                <tr key={name}
                                    className={`border-b border-slate-700/40 last:border-0
                                        ${rowIdx % 2 === 0 ? 'bg-slate-800/40' : 'bg-slate-800/10'}`}>
                                    <EffectNameCell name={name} rowIdx={rowIdx} />
                                    {selectedSkills.map(skill => (
                                        <td key={skill.Id_skill} className="px-4 py-2.5 text-center">
                                            <EffectCell entries={getEffectsFromScenario(skill)[name]} />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </ScrollTable>

                    {/* Legend */}
                    <div className="px-4 py-2 bg-slate-900/30 border-t border-slate-700">
                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {selectedSkills.map((skill, idx) => (
                                <span key={skill.Id_skill} className="flex items-center gap-1.5">
                                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full
                                        text-[8px] font-bold bg-violet-900/60 text-violet-300 border border-violet-700/50">
                                        {idx + 1}
                                    </span>
                                    <span className="text-[10px] text-slate-400 truncate max-w-[160px]"
                                        title={skill.skillName}>{skill.skillName}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EffectsIndividual;
